/* RESPONSIBLE TEAM: team-workflows */
// eslint-disable-next-line no-restricted-imports
import { getFinStandaloneService } from 'embercom/lib/container-lookup';
import {
  type StepConfigGenerationParams,
  type PathConfigGenerationParams,
} from 'embercom/objects/visual-builder/configuration/editor';
import { StandaloneInboundPathConfig } from 'embercom/objects/visual-builder/configuration/path';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import InboundBotEditorConfig from './inbound-bot';
import type Step from 'embercom/models/operator/visual-builder/step';
import { stepTypes } from '../../configuration-list';
import HandoffToZendeskAgentConfiguration from '../step/handoff-to-zendesk-agent';
import HandoffToZendeskTicketConfiguration from '../step/handoff-to-zendesk-ticket';
import { NEW_CONVERSATION_TARGET } from 'embercom/lib/operator/custom-bots/constants';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import { StandaloneIntercomMessagingFinStepConfig } from '../step/fin';
import HandoffToExternalAgentConfiguration from '../step/handoff-to-external-agent';
import HandoffToExternalTicketConfiguration from '../step/handoff-to-external-ticket';
import type Integration from 'embercom/models/standalone/integration';

export default class StandaloneInboundBotEditorConfig extends InboundBotEditorConfig {
  private finStandaloneService: FinStandaloneService;

  constructor({
    areStepPaywallsActive,
    workflow,
  }: {
    areStepPaywallsActive: boolean;
    workflow?: Workflow;
  }) {
    super({ areStepPaywallsActive, workflow });
    this.finStandaloneService = getFinStandaloneService();
  }

  generatePathConfig({ path, editorState }: PathConfigGenerationParams) {
    return new StandaloneInboundPathConfig({
      path,
      editorState,
      supportsLocalAttributes: this.supportsLocalAttributes,
      // TODO: SA Generic platform migration – Make this generic, no need to look for platforms specifically
      isZendeskConfiguredForIntercomMessenger: this.isZendeskConfiguredForIntercomMessenger,
      isSalesforceConfigured: this.isSalesforceConfigured,
      integrations: this.finStandaloneService.standaloneIntegrations,
    });
  }

  generateStepConfig<S extends Step>({ step, pathConfig }: StepConfigGenerationParams<S>) {
    let stepConfigArgs = { step, pathConfig };
    let targetChannels =
      pathConfig?.editorState?.contentEditorService?.activeObject?.targetChannels || [];

    switch (step.typeKey) {
      case stepTypes.fin:
        return new StandaloneIntercomMessagingFinStepConfig(stepConfigArgs);
      case stepTypes.handoffToZendeskAgent:
        return new HandoffToZendeskAgentConfiguration({
          ...stepConfigArgs,
          tooltipCopy: this.intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent-tooltip-ic-messenger',
          ),
          workflowTarget: NEW_CONVERSATION_TARGET,
        });
      case stepTypes.handoffToZendeskTicket:
        return new HandoffToZendeskTicketConfiguration({
          ...stepConfigArgs,
          shouldCheckZendeskApiConnection: true,
        });
      case stepTypes.handoffToExternalAgent: {
        let integration = this.integrations.find((integration) =>
          targetChannels.some((channel: string) =>
            integration.supportsExternalAgentHandoverFromChannels?.includes(channel),
          ),
        );
        return new HandoffToExternalAgentConfiguration({
          ...stepConfigArgs,
          integrationId: integration?.integrationId ?? undefined,
        });
      }
      case stepTypes.handoffToExternalTicket: {
        let integration = this.integrations.find((integration) =>
          targetChannels.some((channel: string) =>
            integration.supportsExternalTicketHandoverFromChannels?.includes(channel),
          ),
        );

        return new HandoffToExternalTicketConfiguration({
          ...stepConfigArgs,
          integrationId: integration?.integrationId ?? undefined,
        });
      }
      default:
        return super.generateStepConfig(stepConfigArgs);
    }
  }

  get isZendeskConfiguredForIntercomMessenger(): boolean {
    if (!this.app.hasStandalonePlatform('zendesk')) {
      return false;
    }

    return this.finStandaloneService.platform.isConnected;
  }

  get isSalesforceConfigured(): boolean {
    if (!this.app.hasStandalonePlatform('salesforce')) {
      return false;
    }

    return this.finStandaloneService.platform.isConnected;
  }

  get integrations(): Integration[] {
    return this.finStandaloneService.standaloneIntegrations;
  }

  get supportsSideSheet(): boolean {
    return true;
  }
}
